/**
 * App showcase — the real thing (AI-11994 rebuild).
 * Five actual iPhone captures of the shipping TestFlight build replace the
 * previous unsplash-photo grid: one featured chat screen with a glass
 * caption card, then the remaining four in a staggered row (horizontal
 * snap carousel on mobile). Copy comes from the screenshot manifest.
 * Depth: sky-fade section background, radial glows, blue-tinted phone
 * shadows, scroll fade-ins (reduced-motion aware via FadeIn).
 */
import manifest from "@/assets/screens/manifest.json";
import bibleShot from "@/assets/screens/bible.png";
import chatShot from "@/assets/screens/chat.png";
import homeShot from "@/assets/screens/home.png";
import shareShot from "@/assets/screens/share.png";
import trackerShot from "@/assets/screens/tracker.png";
import { DeviceFrame } from "./DeviceFrame";
import { FadeIn } from "./FadeIn";

type Screen = { file: string; title: string; caption: string };

const SHOT_SRC: Record<string, string> = {
  "home.png": homeShot,
  "bible.png": bibleShot,
  "chat.png": chatShot,
  "tracker.png": trackerShot,
  "share.png": shareShot,
};

const SCREENS = (manifest as Screen[]).filter((screen) => SHOT_SRC[screen.file]);
const FEATURED = SCREENS.find((screen) => screen.file === "chat.png") ?? SCREENS[0];
const REST = SCREENS.filter((screen) => screen.file !== FEATURED.file);

export function AppShowcaseSection() {
  return (
    <section id="app" className="relative overflow-hidden bg-sky-fade">
      {/* Ambient glow anchoring the featured phone */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 h-[560px] w-[900px] -translate-x-1/2 bg-sky-glow blur-3xl"
      />

      {/* Featured screen: copy + large phone */}
      <div className="relative mx-auto max-w-7xl px-6 pt-20 md:px-12 md:pt-28 lg:px-20">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#3b6fb5]">
              Real screens · on a phone today
            </p>
            <h2 className="mt-5 max-w-xl font-serif text-4xl font-medium tracking-tight text-[#121823] md:text-6xl">
              Designed for the walk.
            </h2>
            <p className="mt-6 max-w-md text-base leading-7 text-neutral-500">
              Not mockups — these are captures from the app as it runs right now. Scripture-first,
              prayerful, personal, and built to go with you.
            </p>
            <div className="mt-10 max-w-md rounded-[28px] border border-white/70 bg-white/60 p-6 shadow-glass backdrop-blur-sm md:p-7">
              <h3 className="text-xl font-semibold tracking-tight text-[#121823]">{FEATURED.title}</h3>
              <p className="mt-3 text-sm leading-6 text-neutral-600">{FEATURED.caption}</p>
            </div>
          </FadeIn>
          <FadeIn delay={120} className="flex justify-center lg:justify-end lg:pr-6">
            <DeviceFrame
              src={SHOT_SRC[FEATURED.file]}
              alt={`App screenshot — ${FEATURED.title}`}
              loading="eager"
              className="w-[248px] sm:w-[288px] md:w-[318px]"
            />
          </FadeIn>
        </div>
      </div>

      {/* The other four: staggered row on desktop, snap carousel on mobile */}
      <div className="relative mx-auto max-w-7xl pt-16 md:pt-24">
        <div className="flex snap-x snap-mandatory gap-8 overflow-x-auto px-6 pb-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:grid md:grid-cols-4 md:gap-8 md:overflow-visible md:px-12 md:pb-0 lg:px-20">
          {REST.map((screen, index) => (
            <FadeIn
              key={screen.file}
              delay={index * 110}
              className={[
                "w-[216px] flex-shrink-0 snap-center md:w-auto",
                index % 2 === 1 ? "md:mt-14" : "",
              ].join(" ")}
            >
              <DeviceFrame
                src={SHOT_SRC[screen.file]}
                alt={`App screenshot — ${screen.title}`}
                depth="float"
                loading="eager"
              />
              <h3 className="mt-6 text-base font-semibold tracking-tight text-[#121823]">{screen.title}</h3>
              <p className="mt-2 text-sm leading-6 text-neutral-500">{screen.caption}</p>
            </FadeIn>
          ))}
        </div>
      </div>

      <div className="relative px-6 pb-20 pt-16 text-center md:px-12 md:pt-24 lg:px-20">
        <div className="mx-auto flex max-w-md items-center justify-center gap-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#5f7291]">
          <span className="h-px flex-1 bg-[#c4d5ea]" />
          <span>Less to read. More to feel.</span>
          <span className="h-px flex-1 bg-[#c4d5ea]" />
        </div>
      </div>
    </section>
  );
}
